import { ErrorOutline } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React from "react";
import { ProcessingStatusData } from "src/views/datasets/types";

interface DocumentStatusIndicatorProps {
  /** Record ID for test IDs */
  recordId?: number;
  /** Fallback: chunks with embedding count (used when no status data) */
  chunksWithEmbedding?: number;
  /** Processing status from API (preferred) */
  processingStatus?: ProcessingStatusData | null;
  /** Display variant */
  variant?: "full" | "compact";
  /** Show chunk progress text inline */
  showChunkProgress?: boolean;
}

/**
 * DocumentStatusIndicator - Shared component for displaying document processing status
 *
 * Shows:
 * - Progress bar with percentage during processing
 * - Status chip with substep label (Uploaded → Reading document → OCR → Indexing → Ready)
 * - Chunk progress text ("15/47 chunks indexed") visible inline
 * - CircularProgress spinner in chip for active states
 * - "Failed" chip (red) with error tooltip
 */
const DocumentStatusIndicator: React.FC<DocumentStatusIndicatorProps> = ({
  recordId,
  chunksWithEmbedding: _chunksWithEmbedding = 0,
  processingStatus,
  variant = "full",
  showChunkProgress: _showChunkProgress = true,
}) => {
  const theme = useTheme();

  // Map display_status to user-friendly labels and colors
  const getStatusDisplay = (
    displayStatus: string,
  ): { label: string; color: string; isActive: boolean } => {
    const statusMap: Record<
      string,
      { label: string; color: string; isActive: boolean }
    > = {
      uploaded: {
        label: "Uploaded",
        color: theme.palette.grey[500],
        isActive: false,
      },
      converting_to_pdf: {
        label: "Reading document",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      chunking: {
        label: "Reading document",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      "reading document": {
        label: "Reading document",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      ocr_queued: {
        label: "OCR queued",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      "ocr queued": {
        label: "OCR queued",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      ocr_processing: {
        label: "OCR document",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      "ocr processing": {
        label: "OCR document",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      embedding: {
        label: "Indexing",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      indexing: {
        label: "Indexing",
        color: theme.palette.secondary.main,
        isActive: true,
      },
      completed: {
        label: "Ready",
        color: theme.palette.success.main,
        isActive: false,
      },
      ready: {
        label: "Ready",
        color: theme.palette.success.main,
        isActive: false,
      },
      failed: {
        label: "Failed",
        color: theme.palette.error.main,
        isActive: false,
      },
    };
    if (statusMap[displayStatus]) {
      return statusMap[displayStatus];
    }
    // Stage-specific failure display strings come through as
    // "Failed: <stage>" (e.g. "Failed: OCR") from the backend's
    // STATUS_DISPLAY_MAP. Match the "failed:" prefix so any new stage
    // gets the error styling automatically (issue #18798).
    if (
      displayStatus.startsWith("failed:") ||
      displayStatus.startsWith("failed ")
    ) {
      const KNOWN_ACRONYMS = new Set(["ocr", "pdf", "docx"]);
      const rawSuffix = displayStatus.replace(/^failed[: ]\s*/, "").trim();
      const formattedSuffix = rawSuffix
        .split(/\s+/)
        .map((word) =>
          KNOWN_ACRONYMS.has(word)
            ? word.toUpperCase()
            : word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join(" ");
      return {
        label: rawSuffix ? `Failed: ${formattedSuffix}` : "Failed",
        color: theme.palette.error.main,
        isActive: false,
      };
    }
    return {
      label: displayStatus,
      color: theme.palette.grey[500],
      isActive: false,
    };
  };

  // No status data - hide indicator (document is ready or no data yet)
  if (!processingStatus) {
    return null;
  }

  const { display_status, is_processing, error_message, progress_percentage } =
    processingStatus;
  // Normalize display_status to lowercase for consistent matching
  const normalizedStatus = display_status?.toLowerCase() || "";
  const statusDisplay = getStatusDisplay(normalizedStatus);

  // Failed status - show error chip with tooltip. Matches the generic
  // "Failed" label and any stage-specific "Failed: <stage>" variant
  // (issue #18798).
  const isFailedDisplay =
    normalizedStatus === "failed" ||
    normalizedStatus.startsWith("failed:") ||
    normalizedStatus.startsWith("failed ");
  if (isFailedDisplay) {
    return (
      <Tooltip title={error_message || "Processing failed"} arrow>
        <Chip
          icon={<ErrorOutline sx={{ fontSize: 14 }} />}
          label={statusDisplay.label}
          size="small"
          data-testid={recordId ? `status-chip-${recordId}` : undefined}
          sx={{
            height: 22,
            fontSize: "0.7rem",
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.dark,
            fontWeight: 500,
            cursor: "help",
            "& .MuiChip-icon": {
              color: theme.palette.error.main,
            },
          }}
        />
      </Tooltip>
    );
  }

  // Completed status - hide indicator (no need to show "Ready" badge)
  if (normalizedStatus === "completed" || normalizedStatus === "ready") {
    return null;
  }

  // Processing status - show progress bar + status chip
  if (is_processing || statusDisplay.isActive) {
    if (variant === "compact") {
      // Compact: just show chip with spinner
      return (
        <Chip
          icon={<CircularProgress size={12} thickness={4} />}
          label={statusDisplay.label}
          size="small"
          data-testid={recordId ? `status-chip-${recordId}` : undefined}
          sx={{
            height: 22,
            fontSize: "0.7rem",
            bgcolor: alpha(statusDisplay.color, 0.1),
            color: statusDisplay.color,
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: statusDisplay.color,
            },
          }}
        />
      );
    }

    // Full variant: progress bar + chip
    return (
      <Stack spacing={0.5} sx={{ minWidth: 120 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progress_percentage}
            sx={{
              flex: 1,
              height: 4,
              borderRadius: 2,
              bgcolor: alpha(statusDisplay.color, 0.1),
              "& .MuiLinearProgress-bar": {
                bgcolor: statusDisplay.color,
                borderRadius: 2,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", color: "text.secondary", minWidth: 28 }}
          >
            {progress_percentage}%
          </Typography>
        </Box>
        <Chip
          icon={<CircularProgress size={12} thickness={4} />}
          label={statusDisplay.label}
          size="small"
          data-testid={recordId ? `status-chip-${recordId}` : undefined}
          sx={{
            height: 20,
            fontSize: "0.65rem",
            bgcolor: alpha(statusDisplay.color, 0.1),
            color: statusDisplay.color,
            fontWeight: 500,
            "& .MuiChip-icon": {
              color: statusDisplay.color,
            },
          }}
        />
      </Stack>
    );
  }

  // Default: show status chip
  return (
    <Chip
      label={statusDisplay.label}
      size="small"
      data-testid={recordId ? `status-chip-${recordId}` : undefined}
      sx={{
        height: 22,
        fontSize: "0.7rem",
        bgcolor: alpha(statusDisplay.color, 0.1),
        color: statusDisplay.color,
        fontWeight: 500,
      }}
    />
  );
};

export default DocumentStatusIndicator;
